import { createAction } from '@reduxjs/toolkit';
import { IErrorType } from '../../constants/types';
import type {
    IAdminPayoutRequestsResponse,
    IGetAdminPayoutRequestsParams,
    IPayoutBank,
    IPayoutCountrySupport,
    IPayoutStripeConfig,
    IPayoutSupportedCountriesData,
    IExecuteAdminPayoutArgs,
    IExecuteAdminPayoutResult,
    IResolveAdminPayoutArgs,
    IVerifyAccountPayload,
    IVerifyAccountResult,
} from '../../types/global/payout.types';

export const types = {
    GET_SUPPORTED_COUNTRIES: 'GET_SUPPORTED_COUNTRIES',
    GET_SUPPORTED_COUNTRIES_SUCCESS: 'GET_SUPPORTED_COUNTRIES_SUCCESS',
    GET_SUPPORTED_COUNTRIES_FAIL: 'GET_SUPPORTED_COUNTRIES_FAIL',

    GET_COUNTRY_SUPPORT: 'GET_COUNTRY_SUPPORT',
    GET_COUNTRY_SUPPORT_SUCCESS: 'GET_COUNTRY_SUPPORT_SUCCESS',
    GET_COUNTRY_SUPPORT_FAIL: 'GET_COUNTRY_SUPPORT_FAIL',

    GET_PAYOUT_BANKS: 'GET_PAYOUT_BANKS',
    GET_PAYOUT_BANKS_SUCCESS: 'GET_PAYOUT_BANKS_SUCCESS',
    GET_PAYOUT_BANKS_FAIL: 'GET_PAYOUT_BANKS_FAIL',

    GET_STRIPE_CONFIG: 'GET_STRIPE_CONFIG',
    GET_STRIPE_CONFIG_SUCCESS: 'GET_STRIPE_CONFIG_SUCCESS',
    GET_STRIPE_CONFIG_FAIL: 'GET_STRIPE_CONFIG_FAIL',

    VERIFY_ACCOUNT: 'VERIFY_ACCOUNT',
    VERIFY_ACCOUNT_SUCCESS: 'VERIFY_ACCOUNT_SUCCESS',
    VERIFY_ACCOUNT_FAIL: 'VERIFY_ACCOUNT_FAIL',
    CLEAR_VERIFY_ACCOUNT: 'CLEAR_VERIFY_ACCOUNT',

    GET_ADMIN_PAYOUT_REQUESTS: 'GET_ADMIN_PAYOUT_REQUESTS',
    GET_ADMIN_PAYOUT_REQUESTS_SUCCESS: 'GET_ADMIN_PAYOUT_REQUESTS_SUCCESS',
    GET_ADMIN_PAYOUT_REQUESTS_FAIL: 'GET_ADMIN_PAYOUT_REQUESTS_FAIL',

    RESOLVE_ADMIN_PAYOUT: 'RESOLVE_ADMIN_PAYOUT',
    RESOLVE_ADMIN_PAYOUT_SUCCESS: 'RESOLVE_ADMIN_PAYOUT_SUCCESS',
    RESOLVE_ADMIN_PAYOUT_FAIL: 'RESOLVE_ADMIN_PAYOUT_FAIL',

    EXECUTE_ADMIN_PAYOUT: 'EXECUTE_ADMIN_PAYOUT',
    EXECUTE_ADMIN_PAYOUT_SUCCESS: 'EXECUTE_ADMIN_PAYOUT_SUCCESS',
    EXECUTE_ADMIN_PAYOUT_FAIL: 'EXECUTE_ADMIN_PAYOUT_FAIL',
} as const;

export interface IGetPayoutBanksParams {
    currency?: string;
    type?: string;
    country?: string;
    enabled_for_verification?: boolean;
    cacheKey?: string;
}

export interface IGetPayoutBanksResult {
    banks: IPayoutBank[];
    cacheKey: string;
}

export const getSupportedCountries = createAction(types.GET_SUPPORTED_COUNTRIES);
export const getSupportedCountriesSuccess = createAction<IPayoutSupportedCountriesData>(
    types.GET_SUPPORTED_COUNTRIES_SUCCESS,
);
export const getSupportedCountriesFail = createAction<IErrorType>(types.GET_SUPPORTED_COUNTRIES_FAIL);

export const getCountrySupport = createAction<string>(types.GET_COUNTRY_SUPPORT);
export const getCountrySupportSuccess = createAction<IPayoutCountrySupport>(types.GET_COUNTRY_SUPPORT_SUCCESS);
export const getCountrySupportFail = createAction<IErrorType>(types.GET_COUNTRY_SUPPORT_FAIL);

export const getPayoutBanks = createAction<IGetPayoutBanksParams>(types.GET_PAYOUT_BANKS);
export const getPayoutBanksSuccess = createAction<IGetPayoutBanksResult>(types.GET_PAYOUT_BANKS_SUCCESS);
export const getPayoutBanksFail = createAction<IErrorType>(types.GET_PAYOUT_BANKS_FAIL);

export const getStripeConfig = createAction<string>(types.GET_STRIPE_CONFIG);
export const getStripeConfigSuccess = createAction<IPayoutStripeConfig>(types.GET_STRIPE_CONFIG_SUCCESS);
export const getStripeConfigFail = createAction<IErrorType>(types.GET_STRIPE_CONFIG_FAIL);

export const verifyPayoutAccount = createAction<IVerifyAccountPayload>(types.VERIFY_ACCOUNT);
export const verifyPayoutAccountSuccess = createAction<IVerifyAccountResult>(types.VERIFY_ACCOUNT_SUCCESS);
export const verifyPayoutAccountFail = createAction<IErrorType>(types.VERIFY_ACCOUNT_FAIL);
export const clearVerifyPayoutAccount = createAction(types.CLEAR_VERIFY_ACCOUNT);

export const getAdminPayoutRequests = createAction<IGetAdminPayoutRequestsParams>(types.GET_ADMIN_PAYOUT_REQUESTS);
export const getAdminPayoutRequestsSuccess = createAction<IAdminPayoutRequestsResponse>(
    types.GET_ADMIN_PAYOUT_REQUESTS_SUCCESS,
);
export const getAdminPayoutRequestsFail = createAction<IErrorType>(types.GET_ADMIN_PAYOUT_REQUESTS_FAIL);

export const resolveAdminPayout = createAction<IResolveAdminPayoutArgs>(types.RESOLVE_ADMIN_PAYOUT);
export const resolveAdminPayoutSuccess = createAction<string>(types.RESOLVE_ADMIN_PAYOUT_SUCCESS);
export const resolveAdminPayoutFail = createAction<IErrorType>(types.RESOLVE_ADMIN_PAYOUT_FAIL);

export const executeAdminPayout = createAction<IExecuteAdminPayoutArgs>(types.EXECUTE_ADMIN_PAYOUT);
export const executeAdminPayoutSuccess = createAction<IExecuteAdminPayoutResult>(types.EXECUTE_ADMIN_PAYOUT_SUCCESS);
export const executeAdminPayoutFail = createAction<{ requestId: string; error: IErrorType }>(
    types.EXECUTE_ADMIN_PAYOUT_FAIL,
);
